<template>
    <form-validation v-slot="{ bindings: validationBindings }">
        <flex-container :gap="Size.lg">
            <flex-container justify-content="space-between" align-items="center">
                <flex-container
                    direction="column"
                    size="auto"
                    :gap="Size.xs"
                >
                    <text-heading>
                        AI rapport voor {{ report.company.name }}
                    </text-heading>
                    <text-description>
                        Rol: {{ report.role.name }}
                        &middot;
                        Periode: {{ formatDate(report.fromDate) }} - {{ formatDate(report.untilDate) }}
                        &middot;
                        Aangemaakt op: {{ formatDate(report.createdAt) }}
                        <template v-if="report.isApproved && report.approvedAt !== null">
                            &middot;
                            Goedgekeurd op: {{ formatDateTime(report.approvedAt) }}
                            <template v-if="report.approvedBy">
                                door {{ report.approvedBy.name.fullName }}
                            </template>
                        </template>
                    </text-description>
                </flex-container>

                <flex-container
                    v-if="!report.isApproved"
                    size="auto"
                    justify-content="flex-end"
                >
                    <button-block
                        :disabled="!saveRequired || !validationBindings.isValid.value"
                        @loading="onSave($event)"
                    >
                        <svg-icon :icon="IconSave" />
                        <span>Aangepaste tekst opslaan</span>
                    </button-block>

                    <button-block
                        :disabled="saveRequired || !validationBindings.isValid.value"
                        @loading="onApprove($event)"
                    >
                        <svg-icon :icon="IconPublish" :color="Color.white" />
                        <span>Akkoord voor verzending</span>
                    </button-block>
                </flex-container>
            </flex-container>
        </flex-container>

        <horizontal-line />

        <flex-container style="margin-top: 2rem">
            <flex-container :size="3">
                <card-normal>
                    <template #sections>
                        <card-section area="card-header">
                            <text-heading>
                                Teams
                            </text-heading>
                        </card-section>
                    </template>

                    <chip-container>
                        <chip-label v-for="team in sortedTeams" :key="team.id">
                            {{ team.name }}
                        </chip-label>
                    </chip-container>
                </card-normal>
            </flex-container>

            <flex-container :size="3">
                <card-normal>
                    <template #sections>
                        <card-section area="card-header">
                            <text-heading>
                                Pijplijnen
                            </text-heading>
                        </card-section>
                    </template>

                    <chip-container>
                        <template v-if="sortedPipelines.length === 0">
                            <chip-label>
                                Alle pijplijnen
                            </chip-label>
                        </template>
                        <template v-else>
                            <chip-label v-for="pipeline in sortedPipelines" :key="pipeline.id">
                                {{ pipeline.name }}
                            </chip-label>
                        </template>
                    </chip-container>
                </card-normal>
            </flex-container>

            <flex-container :size="3">
                <card-normal>
                    <template #sections>
                        <card-section area="card-header">
                            <text-heading>
                                Ontvangers
                            </text-heading>
                        </card-section>
                    </template>

                    <chip-container>
                        <chip-avatar v-for="recipient in sortedRecipients" :key="recipient.id">
                            <user-avatar
                                :placeholder="recipient.name.abbreviation"
                                size="md"
                                :url="recipient.avatar ? resizedToSizeUploadUrl(recipient.avatar, 'md') : ''"
                                :alt="recipient.name"
                            />
                            <span>{{ recipient.name.fullName }}</span>
                        </chip-avatar>
                    </chip-container>
                </card-normal>
            </flex-container>

            <flex-container :size="3">
                <card-normal>
                    <template #sections>
                        <card-section area="card-header">
                            <text-heading>
                                Threads
                            </text-heading>
                        </card-section>
                    </template>

                    <chip-container>
                        <chip-label v-for="threadId in report.threadIds" :key="threadId">
                            {{ threadId }}
                        </chip-label>
                    </chip-container>
                </card-normal>
            </flex-container>
        </flex-container>

        <flex-container style="margin-top: 2rem">
            <flex-container :size="5">
                <card-normal>
                    <template #sections>
                        <card-section area="card-header">
                            <text-heading>
                                Originele tekst
                            </text-heading>
                        </card-section>
                    </template>

                    <flex-container>
                        <input-group :model-value="originalContent">
                            <template #input>
                                <rich-text-area-input
                                    :mentionables="[]"
                                    size="lg"
                                    toolbar="extended"
                                    markdown
                                    disabled
                                />
                            </template>
                        </input-group>
                    </flex-container>
                </card-normal>
            </flex-container>

            <flex-container :size="5">
                <card-normal>
                    <template #sections>
                        <card-section area="card-header">
                            <text-heading>
                                Aangepaste tekst
                            </text-heading>
                        </card-section>
                    </template>

                    <flex-container>
                        <input-group v-model:model-value="modifiedContent">
                            <template #input>
                                <rich-text-area-input
                                    :mentionables="[]"
                                    size="lg"
                                    toolbar="extended"
                                    markdown
                                    :disabled="report.isApproved"
                                />
                            </template>
                        </input-group>
                    </flex-container>
                </card-normal>
            </flex-container>
        </flex-container>
    </form-validation>
</template>

<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { CardSection, Color, FlexContainer, FormValidation, InputGroup, Size, TextHeading } from '@keyvue/one-ui';
    import { Report, ApproveReportCommand, ChangeReportContentCommand } from '@keyvue/one-api-client';
    import IconSave from '@keyvue/one-material-icons/icons/save.svg?component';
    import IconPublish from '@keyvue/one-material-icons/icons/publish.svg?component';

    import useClient from '@composables/useClient';
    import useNotify from '@composables/useNotify';
    import { formatDate, formatDateTime } from '@/utils/date';
    import { resizedToSizeUploadUrl } from '@/utils/resize';

    interface Props {
        reportId: string;
    }

    const { notifySaving, notifyError } = useNotify();
    const { client } = useClient();

    const props = defineProps<Props>();
    const report = ref<Report>(await client.get(Report, props.reportId));
    const originalContent = ref(report.value.originalContent);
    const modifiedContent = ref(report.value.modifiedContent ?? report.value.originalContent);

    const sortedTeams = computed(() => report.value.teams.slice().sort((a, b) => a.name.localeCompare(b.name)));
    const sortedPipelines = computed(() => report.value.pipelines.slice().sort((a, b) => a.name.localeCompare(b.name)));
    const sortedRecipients = computed(() => report.value.recipients.slice().sort((a, b) => a.name.fullName.localeCompare(b.name.fullName)));

    const saveRequired = computed(() => report.value.modifiedContent !== modifiedContent.value);

    const reloadData = async () => {
        report.value = await client.get(Report, props.reportId);
        originalContent.value = report.value.originalContent;
        modifiedContent.value = report.value.modifiedContent ?? report.value.originalContent;
    };

    const onSave = async (event: any) => {
        const closeNotification = notifySaving('Bezig met opslaan...', 0);

        try {
            await client.dispatch(ChangeReportContentCommand.fromReport({ ...report.value, modifiedContent: modifiedContent.value } as Report));
            await reloadData();
        } catch (error: any) {
            notifyError(error.message);
        } finally {
            closeNotification();
            event.button.setState('completed');
        }
    };

    const onApprove = async (event: any) => {
        const closeNotification = notifySaving('Bezig met goedkeuren...', 0);

        try {
            await client.dispatch(ApproveReportCommand.fromReport(report.value as Report));
            await reloadData();
        } catch (error: any) {
            notifyError(error.message);
        } finally {
            closeNotification();
            event.button.setState('completed');
        }
    };
</script>
